import * as React from 'react';
import { compose, Dispatch, bindActionCreators } from 'redux';
import { connect } from 'react-redux';

import * as Yup from 'yup';
import { withFormik, FormikProps, Form, Field } from 'formik';
import { RouteComponentProps } from 'react-router-dom';

import {
  createStyles,
  withStyles,
  WithStyles,
  FormGroup,
  FormControlLabel,
  Switch
} from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';

import CopyIcon from '@material-ui/icons/FileCopy';

import { ILicense, LicenseOs } from '../../@types';
import withEditContainer, {
  IEditContainedProps
} from '../../common/edit-container/edit-container';
import FormikSelect from '../../common/formik/formik-select';
import FormikTextField from '../../common/formik/formik-text-field';
import FormikUsdFormat from '../../common/formik/formik-usd-format';
import EditIcons from '../../common/edit-icons/edit-icons';
import DeleteDialog from '../../common/delete-dialog/delete-dialog';

import * as actions from '../../modules/license/action';

export interface ILicenseFormValues {
  bundleId: string;
  costInitial: number | '';
  costRenewal: number | '';
  expirationDate: string;
  expireAction: string;
  terms: string;
  appName: string;
  os: LicenseOs;
}

interface ILicenseRouterProps {
  customerId: string;
}

const formikEnhancer = withFormik<
  IEditContainedProps & RouteComponentProps<ILicenseRouterProps>,
  ILicenseFormValues
>({
  mapPropsToValues: props => {
    const license = props.entity as ILicense;
    const { isCopy } = props;

    if (isCopy) {
      license.id = undefined;
      license.licenseKey = '';
      license.os === 'Android'
        ? (license.os = 'iOS')
        : (license.os = 'Android');
    }

    return {
      bundleId: license.bundleId || '',
      costInitial: license.costInitial || '',
      costRenewal: license.costRenewal || '',
      expirationDate:
        ((license.id || isCopy) &&
          license.expirationDate.toISOString().slice(0, 10)) ||
        '',
      expireAction: license.expireAction || 'WARN',
      terms: license.terms || '',
      appName: license.appName || '',
      os: license.os || 'iOS',
      licenseKey: license.licenseKey || 'N/A'
    };
  },
  handleSubmit: (values, formikActions) => {
    const props = formikActions.props;
    const license = props.entity as ILicense;
    const valuesToLicense: ILicense = {
      id: license.id,
      licenseKey: license.licenseKey,
      numUniqueUsers: license.numUniqueUsers,
      createdDate: new Date(),
      updatedDate: new Date(),
      bundleId: values.bundleId.trim(),
      costInitial: values.costInitial as number,
      costRenewal: values.costRenewal as number,
      customerId: license.customerId || Number(props.match.params.customerId),
      expirationDate: new Date(values.expirationDate),
      expireAction: values.expireAction,
      terms: values.terms,
      appName: values.appName,
      os: values.os,
      status: license.status
    };

    if (props.isCopy) {
      delete valuesToLicense.id;
    }

    props.onEntitySubmit(valuesToLicense, formikActions);
  },
  validationSchema: Yup.object().shape({
    bundleId: Yup.string().required('The Bundle ID is required'),
    costInitial: Yup.number()
      .typeError('The initial cost must be a numeric')
      .required('Enter the initial cost of the license'),
    costRenewal: Yup.number()
      .typeError('The renewal cost must be a numeric')
      .required('Enter the renewal cost of the license'),
    expirationDate: Yup.date()
      .typeError('Enter a valid date')
      .required('Enter the date on which the license will expire'),
    expireAction: Yup.string()
      .oneOf(
        ['WARN', 'SHUTDOWN'],
        'The expire action can only be either WARN or SHUTDOWN'
      )
      .required('You must specify an expire action'),
    terms: Yup.string(),
    appName: Yup.string(),
    os: Yup.string()
  })
});

const styles = createStyles({
  borderRight: {
    borderRight: '1px solid lightgrey'
  },
  hidden: {
    height: 0,
    margin: 0,
    padding: 0,
    border: 0
  }
});

const expireActionOptions = [
  {
    value: 'WARN',
    label: 'Warn - Displays "For Development Use Only" on screen'
  },
  {
    value: 'SHUTDOWN',
    label: 'Shutdown - Any attempt to open the app will fail'
  }
];

const osOptions = [
  {
    value: 'iOS'
  },
  {
    value: 'Android'
  }
];

interface IDispatchProps {
  activate: typeof actions.activateLicense;
  deactivate: typeof actions.deactivateLicense;
}

const LicenseEntry = (
  props: FormikProps<ILicenseFormValues> &
    IEditContainedProps &
    IDispatchProps &
    WithStyles<typeof styles> &
    RouteComponentProps<any>
) => {
  const {
    isSubmitting,
    onEditCancel,
    onEditStart,
    formDisabled,
    classes,
    deleteFn,
    activate,
    deactivate,
    isCopy
  } = props;

  const license: ILicense = props.entity as ILicense;

  const copyLicenseButton = (
    <Tooltip title="Make new copy of license " placement={'bottom'}>
      <span>
        <IconButton
          // tslint:disable-next-line jsx-no-lambda
          onClick={() => props.history.push('/licenses/copy/' + license.id)}
        >
          <CopyIcon />
        </IconButton>
      </span>
    </Tooltip>
  );

  const handleToggle = () => (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      activate(license);
    } else {
      deactivate(license);
    }
  };

  const showActivateToggle = (
    <FormGroup row>
      <FormControlLabel
        control={
          <Switch
            checked={license.status === 'ACTIVE'}
            onChange={handleToggle()}
            color="secondary"
          />
        }
        label={license.status}
        style={{ height: '35px' }}
      />
    </FormGroup>
  );

  const displayToggle = license.id && showActivateToggle;

  return (
    <Form>
      <Card>
        <CardContent>
          <Grid container direction="row" justify="space-between">
            <Grid
              item
              xs={10}
              container
              direction="row"
              alignContent="flex-start"
              spacing={16}
            >
              <Grid item>
                <Typography variant="h6">
                  {license.id
                    ? license.appName
                    : isCopy
                    ? 'Copy ' + license.appName
                    : 'Add License'}
                </Typography>
              </Grid>
              <Grid item>{displayToggle}</Grid>
            </Grid>
            <Grid item container justify="flex-end" xs={2}>
              <Grid item>
                <EditIcons
                  formDisabled={formDisabled}
                  isSubmitting={isSubmitting}
                  showEdit={!!license.id}
                  onEditStart={onEditStart}
                  onEditCancel={onEditCancel}
                  showDelete={!!license.id}
                />
              </Grid>
              <Grid item>{license.id && copyLicenseButton}</Grid>
            </Grid>
          </Grid>
          <Grid container direction="row" spacing={24}>
            <Grid
              item
              container
              direction="column"
              xs={12}
              md={6}
              className={classes.borderRight}
            >
              <Field
                id="license-key-input"
                name="licenseKey"
                label="License Key"
                fullWidth
                disabled
                component={FormikTextField}
              />
              <Field
                id="license-app-name-input"
                name="appName"
                label="App Name"
                fullWidth
                disabled={formDisabled}
                component={FormikTextField}
              />
              <Field
                id="license-os-input"
                name="os"
                label="OS"
                fullWidth
                disabled={formDisabled}
                component={FormikSelect}
                options={osOptions}
              />
              <Field
                id="license-bundle-id-input"
                name="bundleId"
                label="Bundle Id"
                fullWidth
                disabled={formDisabled}
                component={FormikTextField}
              />
              <Field
                id="license-cost-intial-input"
                name="costInitial"
                label="Initial Cost"
                fullWidth
                disabled={formDisabled}
                component={FormikUsdFormat}
              />
              <Field
                id="license-cost-renewal-input"
                name="costRenewal"
                label="Renewal Cost"
                fullWidth
                disabled={formDisabled}
                component={FormikUsdFormat}
              />
              <Field
                id="license-expiration-date-input"
                name="expirationDate"
                label="Expiration Date"
                type="date"
                fullWidth
                disabled={formDisabled}
                component={FormikTextField}
                InputLabelProps={{
                  shrink: true
                }}
              />
              <Field
                id="license-expire-action-input"
                name="expireAction"
                label="Expire Action"
                fullWidth
                disabled={formDisabled}
                component={FormikSelect}
                options={expireActionOptions}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Field
                id="license-terms-input"
                name="terms"
                label="Terms"
                fullWidth
                multiline
                rows={16}
                disabled={formDisabled}
                component={FormikTextField}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      <DeleteDialog
        entity={license}
        title="Delete license?"
        prompt={<p>{license.appName}</p>}
        deleteEntity={deleteFn}
      />
    </Form>
  );
};

const config = {
  get: actions.getLicense,
  update: actions.updateLicense,
  clear: actions.clearLicense,
  deleteFn: actions.deleteLicense,
  submit: actions.submitLicense,
  goBackOnEditEnd: false,
  reducerName: 'licenseReducer',
  entityName: 'license',
  formikEnhancer
};

const mapDispatchToProps = (dispatch: Dispatch<actions.LicenseAction>) => {
  return bindActionCreators(
    {
      activate: actions.activateLicense,
      deactivate: actions.deactivateLicense
    },
    dispatch
  );
};

const enhance = compose(
  withStyles(styles),
  withEditContainer(config),
  connect<null, IDispatchProps>(
    null,
    mapDispatchToProps
  )
);

export default enhance(LicenseEntry);



// WEBPACK FOOTER //
// ./src/features/license-entry/license-entry.tsx